import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { App } from "vue";
import VueAxios from "vue-axios";
import { JwtService } from "@/services/JwtService";
import router from "@/router";
import { localizePath } from "@/plugins/localizedRoutes";

class ApiService {
  public static vueInstance: App;

  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);

    ApiService.vueInstance.axios.defaults.baseURL =
      import.meta.env.VITE_APP_BASE_URL;

    ApiService.vueInstance.axios.defaults.headers.common["Accept-Language"] =
      localStorage.getItem("locale") || "en";
    this.handleResponseError(ApiService.vueInstance.axios);
  }

  public static refreshToken(axios: AxiosInstance) {
    return new Promise((resolve, reject) => {
      const refresh = JwtService.getRefresh();
      if (!refresh) {
        JwtService.destroyAccess();
        const currentLocale = localStorage.getItem("locale") || "en";
        return router.push(localizePath("/auth/login", currentLocale));
      }

      const headersWithoutAuth = { ...axios.defaults.headers };
      headersWithoutAuth.post["Content-Type"] = "application/json";
      delete headersWithoutAuth.common.Authorization;
      axios
        .post<{ refresh: string }, { data: any }>(
          import.meta.env.VITE_APP_BASE_URL + "/user/auth/refresh/",
          {
            refresh: refresh,
          },
          {
            headers: { ...headersWithoutAuth.post },
          },
        )
        .then((response) => {
          JwtService.saveToken(response?.data?.access);
          ApiService.setHeader();
          resolve(response?.data?.access);
        })
        .catch(async (error) => {
          if (error?.code === "ERR_NETWORK") {
            JwtService.destroyRefresh();
            JwtService.destroyAccess();
            const currentLocale = localStorage.getItem("locale") || "en";
            await router.push(localizePath("/auth/login", currentLocale));
            return;
          }
          reject(error);
        });
    });
  }

  public static handleResponseError(axios: AxiosInstance) {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const errorResponse = error?.response;
        const originalRequest = error?.config;

        try {
          if (
            error?.code === "ECONNABORTED" &&
            error?.message === "timeout exceeded"
          ) {
            localStorage.clear();
            const currentLocale = localStorage.getItem("locale") || "en";
            await router.push(localizePath("/auth/login", currentLocale));
            return;
          }

          // Handle Unauthorized or Forbidden
          if (errorResponse?.status === 401 || errorResponse?.status === 403) {
            if (originalRequest && !originalRequest._retry) {
              originalRequest._retry = true;

              const isRefreshError = originalRequest?.url?.includes(
                "/user/auth/refresh/",
              );

              if (isRefreshError) {
                JwtService.destroyAccess();
                JwtService.destroyRefresh();
                const currentLocale = localStorage.getItem("locale") || "en";
                await router.push(localizePath("/auth/login", currentLocale));
                return;
              }

              const newToken = await this.refreshToken(
                ApiService.vueInstance.axios,
              );
              if (newToken) {
                ApiService.setHeader();
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axios(originalRequest);
              } else {
                JwtService.destroyAccess();
                JwtService.destroyRefresh();
                const currentLocale = localStorage.getItem("locale") || "en";
                await router.push(localizePath("/auth/login", currentLocale));
              }
            }
          }

          // Handle Not Found
          if (errorResponse?.status === 404) {
            await router.push({ name: "404" });
            return Promise.reject(error);
          }

          // General rejection for other errors
          return Promise.reject(error);
        } catch (err) {
          return Promise.reject(err);
        }
      },
    );
  }

  public static setHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common["Authorization"] =
      JwtService.getToken() ? `Bearer ${JwtService.getToken()}` : undefined;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
    ApiService.vueInstance.axios.defaults.headers.common["Accept-Language"] =
      localStorage.getItem("locale") || "en";
  }

  public static unsetHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common["Authorization"] = ``;
  }

  public static query<T>(
    resource: string,
    params: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return ApiService?.vueInstance?.axios?.get(resource, params);
  }

  public static get<R = any>(
    resource: string,
    slug = "" as string,
  ): Promise<AxiosResponse<R>> {
    return ApiService?.vueInstance?.axios?.get(`${resource}${slug}`);
  }

  public static post<T = any, R = any>(
    resource: string,
    data?: T,
    params?: AxiosRequestConfig,
  ): Promise<AxiosResponse<R>> {
    return ApiService.vueInstance.axios.post(`${resource}`, data, params);
  }

  public static update<T = any, R = any>(
    resource: string,
    slug: string,
    data?: T,
    params?: AxiosRequestConfig,
  ): Promise<AxiosResponse<R>> {
    return ApiService.vueInstance.axios.put(
      `${resource}/${slug}`,
      data,
      params,
    );
  }

  public static put<T = any, R = any>(
    resource: string,
    data?: T,
    params?: AxiosRequestConfig,
  ): Promise<AxiosResponse<R>> {
    return ApiService.vueInstance.axios.put(`${resource}`, data, params);
  }

  public static patch<T = any, R = any>(
    resource: string,
    data?: T,
    params?: AxiosRequestConfig,
  ): Promise<AxiosResponse<R>> {
    return ApiService.vueInstance.axios.patch(`${resource}`, data, params);
  }

  public static delete<T = any>(
    resource: string,
    data?: T,
    params?: T,
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(`${resource}`, { data, params });
  }
}

export default ApiService;
